import { z } from 'zod';

export const DYNAMIC_BOOKING_FIELD_TYPES = [
	'TEXT',
	'TEXTAREA',
	'NUMBER',
	'TOGGLE',
	'SELECT',
	'EMAIL',
	'PHONE',
] as const;

export type DynamicBookingFieldType = (typeof DYNAMIC_BOOKING_FIELD_TYPES)[number];

/**
 * How a field with a value surfaces on the check-in list row. Mirrors the backend
 * `CheckInDisplay` enum. TAG/ICON reuse the field's abbreviation / icon / colour.
 */
export const CHECK_IN_DISPLAY = ['NONE', 'DOT', 'ICON', 'TAG'] as const;

export type CheckInDisplay = (typeof CHECK_IN_DISPLAY)[number];

/** Human-friendly labels + descriptions for the check-in display picker. */
export const CHECK_IN_DISPLAY_DESCRIPTIONS: Record<CheckInDisplay, { label: string; description: string }> = {
	NONE: { label: 'None', description: 'Not shown in check-in.' },
	DOT: { label: 'Dot', description: 'Red-dot indicator beside the name.' },
	ICON: { label: 'Icon', description: 'Shows the field icon (in its colour) in the check-in row tags.' },
	TAG: {
		label: 'Tag',
		description: 'Shows a coloured chip in the check-in row tags using the abbreviation + colour.',
	},
};

/** Human-friendly label for a check-in display token, shared by the list + detail view. */
export const prettyCheckInDisplay = (value: string) =>
	CHECK_IN_DISPLAY_DESCRIPTIONS[value as CheckInDisplay]?.label ?? value;

/**
 * Human-friendly descriptions surfaced in the field-type picker so admins know
 * exactly what each option produces in the booking wizard. Keep keys in sync
 * with the enum names above.
 */
export const DYNAMIC_BOOKING_FIELD_TYPE_DESCRIPTIONS: Record<
	DynamicBookingFieldType,
	{ label: string; description: string }
> = {
	TEXT: { label: 'Text', description: 'Single-line text input — names, identifiers, short notes.' },
	TEXTAREA: { label: 'Text area', description: 'Multi-line text for longer responses or comments.' },
	NUMBER: { label: 'Number', description: 'Numeric input with a number keyboard on mobile.' },
	TOGGLE: { label: 'Toggle', description: 'Yes / no switch — a single tickable checkbox.' },
	SELECT: { label: 'Select', description: 'Dropdown of pre-defined options that you configure.' },
	EMAIL: { label: 'Email', description: 'Text input with email format validation and email keyboard.' },
	PHONE: { label: 'Phone', description: 'Text input with a phone keyboard for mobile entry.' },
};

/**
 * Booking types admins can target with a dynamic field. EVENT_BOOKING is intentionally
 * excluded in v1 — the booking-wizard event flow doesn't render the dynamic-fields
 * tab yet (see plan).
 */
export const DYNAMIC_FIELD_BOOKING_TYPES = [
	'PASSENGER_ONLY',
	'VEHICLE',
	'VEHICLE_AND_PASSENGER',
	'TRAILER',
	'FREIGHT',
	'PARKING',
] as const;

export type DynamicFieldBookingType = (typeof DYNAMIC_FIELD_BOOKING_TYPES)[number];

/**
 * Human-friendly labels for the raw booking-type tokens, so the list and detail
 * view render "Walk-on" rather than "PASSENGER_ONLY". Unknown tokens fall back to
 * a title-cased form via {@link prettyBookingType}.
 */
export const BOOKING_TYPE_LABELS: Record<string, string> = {
	PASSENGER_ONLY: 'Walk-on',
	VEHICLE: 'Vehicle',
	VEHICLE_AND_PASSENGER: 'Vehicle & passenger',
	TRAILER: 'Trailer',
	FREIGHT: 'Freight',
	PARKING: 'Parking',
};

export const prettyBookingType = (token: string) =>
	BOOKING_TYPE_LABELS[token] ?? token.charAt(0) + token.slice(1).toLowerCase().replace(/_/g, ' ');

/**
 * Human-friendly label for a field-type token (e.g. "TEXT" -> "Text"), shared by
 * the list and detail view. Falls back to the raw token for unknown types.
 */
export const prettyFieldType = (fieldType: string) =>
	DYNAMIC_BOOKING_FIELD_TYPE_DESCRIPTIONS[fieldType as DynamicBookingFieldType]?.label ?? fieldType;

export const dynamicBookingFieldFormSchema = z
	.object({
		label: z.string().min(1, 'Label is required'),
		labelKey: z.string().optional().default(''),
		placeholder: z.string().min(1, 'Placeholder is required'),
		placeholderKey: z.string().optional().default(''),
		tooltipEnabled: z.boolean().default(false),
		tooltipContent: z.string().optional().default(''),
		tooltipContentKey: z.string().optional().default(''),
		icon: z.string().optional().default(''),
		colour: z.string().optional().default(''),
		abbreviation: z.string().optional().default(''),
		bookingTypes: z.array(z.enum(DYNAMIC_FIELD_BOOKING_TYPES)),
		/**
		 * Whether the field is also asked on a charter. A charter is not a booking type — it goes
		 * through none of the booking flows — so it is a flag of its own, and a field can be aimed at
		 * booking types, at charters, or at both.
		 */
		appliesToCharters: z.boolean().default(false),
		required: z.boolean().default(false),
		requiredInCheckIn: z.boolean().default(false),
		checkInDisplay: z.enum(CHECK_IN_DISPLAY).default('NONE'),
		searchableInCheckIn: z.boolean().default(false),
		includedInReports: z.boolean().default(false),
		order: z.union([z.number(), z.nan()]).optional().default(0),
		fieldType: z.enum(DYNAMIC_BOOKING_FIELD_TYPES).default('TEXT'),
		/** Section this field is grouped under in the wizard. null = "Additional information". */
		sectionId: z.string().nullable().optional().default(null),
		selectOptions: z.array(z.string().min(1, 'Option cannot be empty')).optional().default([]),
		/** Customer-type ids (UserDisplayNameEntity.id) this field is restricted to. Empty = visible to everyone. */
		userDisplayNameIds: z.array(z.string()).optional().default([]),
		archived: z.boolean().default(false),
	})
	.superRefine((data, ctx) => {
		// A field aimed at nothing is asked nowhere. The message lands on `bookingTypes` because that
		// is the field the one "Applies to" picker reads its error from — charters are the same picker.
		if (data.bookingTypes.length === 0 && !data.appliesToCharters) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Pick at least one place this field is asked',
				path: ['bookingTypes'],
			});
		}
		if (data.fieldType === 'SELECT' && (!data.selectOptions || data.selectOptions.length === 0)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'SELECT fields need at least one option',
				path: ['selectOptions'],
			});
		}
		if (data.fieldType === 'SELECT' && data.selectOptions) {
			// Flag duplicates case-insensitively and ignoring surrounding whitespace, since
			// those would be indistinguishable in the dropdown. Empties are already caught by
			// the element-level min(1) check, so skip them here to avoid double-flagging. The
			// issue is attached to each repeat occurrence's index so it renders under that row.
			const seen = new Set<string>();
			data.selectOptions.forEach((option, index) => {
				const normalised = option.trim().toLowerCase();
				if (normalised === '') {
					return;
				}
				if (seen.has(normalised)) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Options must be unique',
						path: ['selectOptions', index],
					});
				} else {
					seen.add(normalised);
				}
			});
		}
		if (data.checkInDisplay === 'TAG' && (!data.abbreviation || data.abbreviation.trim() === '')) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'A tag needs an abbreviation to display in check-in',
				path: ['abbreviation'],
			});
		}
		if (data.checkInDisplay === 'ICON' && (!data.icon || data.icon.trim() === '')) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'An icon display needs an icon to show in check-in',
				path: ['icon'],
			});
		}
		// DOT / TAG / ICON all render in the field's colour, so it's required for them.
		if (data.checkInDisplay !== 'NONE' && (!data.colour || data.colour.trim() === '')) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Pick a colour for the check-in display',
				path: ['colour'],
			});
		}
		if (data.tooltipEnabled && (!data.tooltipContent || data.tooltipContent.trim() === '')) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Tooltip content is required when the tooltip is shown',
				path: ['tooltipContent'],
			});
		}
	});

export type DynamicBookingFieldFormValues = z.infer<typeof dynamicBookingFieldFormSchema>;
export const DYNAMIC_BOOKING_FIELD_FORM_ID = 'dynamic-booking-field-crud-form';

/** Each join row carries its own id (for stable EF merges) + the foreign keys. */
export interface DynamicBookingFieldUserDisplayNameDto {
	id: string;
	dynamicBookingFieldId: string;
	userDisplayNameId: string;
}

/** Backend DTO shape. booking types stay as CSV; user types use the join collection. */
export interface DynamicBookingFieldEntityDto {
	id: string;
	label: string;
	labelKey: string | null;
	placeholder: string | null;
	placeholderKey: string | null;
	tooltipEnabled: boolean;
	tooltipContent: string | null;
	tooltipContentKey: string | null;
	icon: string | null;
	colour: string | null;
	abbreviation: string | null;
	bookingTypes: string; // CSV
	appliesToCharters: boolean;
	required: boolean;
	requiredInCheckIn: boolean;
	checkInDisplay: CheckInDisplay;
	searchableInCheckIn: boolean;
	includedInReports: boolean;
	order: number;
	fieldType: DynamicBookingFieldType;
	sectionId: string | null;
	selectOptions: string | null; // JSON array of strings
	archived: boolean;
	userDisplayNames: DynamicBookingFieldUserDisplayNameDto[];
}

/** Convert backend DTO into the form values shape. */
export function dtoToForm(dto: DynamicBookingFieldEntityDto): DynamicBookingFieldFormValues {
	const parseJsonArray = <T>(raw: string | null | undefined): T[] => {
		if (!raw) return [];
		try {
			return JSON.parse(raw) as T[];
		} catch {
			return [];
		}
	};

	const parseCsv = (raw: string | null | undefined): string[] => {
		if (!raw) return [];
		return raw
			.split(',')
			.map(s => s.trim())
			.filter(Boolean);
	};

	return {
		label: dto.label ?? '',
		labelKey: dto.labelKey ?? '',
		placeholder: dto.placeholder ?? '',
		placeholderKey: dto.placeholderKey ?? '',
		tooltipEnabled: !!dto.tooltipEnabled,
		tooltipContent: dto.tooltipContent ?? '',
		tooltipContentKey: dto.tooltipContentKey ?? '',
		icon: dto.icon ?? '',
		colour: dto.colour ?? '',
		abbreviation: dto.abbreviation ?? '',
		bookingTypes: parseCsv(dto.bookingTypes).filter((t): t is DynamicFieldBookingType =>
			(DYNAMIC_FIELD_BOOKING_TYPES as readonly string[]).includes(t),
		),
		appliesToCharters: !!dto.appliesToCharters,
		required: !!dto.required,
		requiredInCheckIn: !!dto.requiredInCheckIn,
		checkInDisplay: dto.checkInDisplay ?? 'NONE',
		searchableInCheckIn: !!dto.searchableInCheckIn,
		includedInReports: !!dto.includedInReports,
		order: dto.order ?? 0,
		fieldType: dto.fieldType ?? 'TEXT',
		sectionId: dto.sectionId ?? null,
		selectOptions: parseJsonArray<string>(dto.selectOptions),
		userDisplayNameIds: (dto.userDisplayNames ?? []).map(j => j.userDisplayNameId),
		archived: !!dto.archived,
	};
}

/**
 * Convert form values back to a partial DTO ready to send to the API. The
 * userDisplayNameIds array becomes a fresh join-row collection (server-side
 * MergeReferences replaces the prior set on save).
 */
export function formToDto(
	values: DynamicBookingFieldFormValues,
	existingId?: string,
): Partial<DynamicBookingFieldEntityDto> {
	return {
		label: values.label,
		labelKey: values.labelKey || null,
		placeholder: values.placeholder || null,
		placeholderKey: values.placeholderKey || null,
		tooltipEnabled: values.tooltipEnabled,
		tooltipContent: values.tooltipContent || null,
		tooltipContentKey: values.tooltipContentKey || null,
		icon: values.icon || null,
		colour: values.colour || null,
		abbreviation: values.abbreviation || null,
		bookingTypes: values.bookingTypes.join(','),
		appliesToCharters: values.appliesToCharters,
		required: values.required,
		requiredInCheckIn: values.requiredInCheckIn,
		checkInDisplay: values.checkInDisplay,
		searchableInCheckIn: values.searchableInCheckIn,
		includedInReports: values.includedInReports,
		order: Number.isFinite(values.order) ? Number(values.order) : 0,
		fieldType: values.fieldType,
		sectionId: values.sectionId || null,
		selectOptions: values.fieldType === 'SELECT' ? JSON.stringify(values.selectOptions ?? []) : null,
		archived: values.archived,
		userDisplayNames: (values.userDisplayNameIds ?? []).map(udnId => ({
			// Fresh join rows on every save — server MergeReferences cleans removed ones.
			id: '00000000-0000-0000-0000-000000000000',
			dynamicBookingFieldId: existingId ?? '00000000-0000-0000-0000-000000000000',
			userDisplayNameId: udnId,
		})),
	};
}
